import { DestinationDistance } from "pages/api/distance-search";
import { StoryblokDataSource, VacancyProps } from "storyblok/components";
import { parseISO } from "date-fns";
import { uniq } from "lodash";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";

import { ARROW_DOWN, Button } from "components/ui";
import { VacancyOverviewFilters } from "components/vacancies/VacancyOverview/VacancyOverviewFilters/VacancyOverviewFilters";

import {
    Cards,
    DesktopOnly,
    Left,
    LeftContent,
    MobileContent,
    MobileOnly,
    Right,
    RightDescription,
    RightTitle,
    StyledButton,
    StyledContainer,
    Wrapper
} from "./VacancyOverview.styled";
import { EmptyCard } from "./EmptyCard/EmptyCard";
import { generateVacancyOverviewCard } from "./VacancyOverviewCard/VacancyOverviewCard";

import { useBodyScrollLock, useMediaQueryContext } from "helpers/hooks";
import { useFilterOptions } from "helpers/hooks/useFilterOptions";

type PageProps = {
    initialVacancies: Array<VacancyProps>;
    isZzp: boolean;
    departmentSalaries: Record<string, string>;
    // Label of results shown in with first letter capitalized, e.g. "Vacatures"
    label?: string | undefined;
    /** departments are only applicable to non-zzp vacancies */
    departments?: Array<StoryblokDataSource>;
    employmentTypes?: Array<StoryblokDataSource>;
    regions: Array<string>;
    destinationsWithDistances?: Array<DestinationDistance>;
    setDestinationsWithDistances: (
        value: Array<DestinationDistance> | undefined
    ) => void;
};

function sortByDate(a: VacancyProps, b: VacancyProps) {
    return (
        parseISO(b.created_at || "").getTime() -
        parseISO(a.created_at || "").getTime()
    );
}

export function VacancyOverview({
    initialVacancies,
    isZzp,
    departmentSalaries,
    label = "Vacatures",
    departments,
    employmentTypes,
    regions,
    destinationsWithDistances,
    setDestinationsWithDistances
}: PageProps): JSX.Element {
    const { isMobileView } = useMediaQueryContext();
    const router = useRouter();
    const initialShownAmount = 10;

    const [showFilters, setShowFilters] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [maxShown, setMaxShown] = useState(initialShownAmount);
    const [calculatingFilters, setCalculatingFilters] = useState(false);

    const [searchTerm, setSearchTerm] = useState<string | null>(null);
    const [vacancies, setVacancies] = useState<Array<VacancyProps>>([]);
    const [zzp, setZzp] = useState(isZzp || false);

    const [loading, setLoading] = useState(false);
    const [noResults, setNoResults] = useState(false);

    //context that takes the selected filter options and makes them available to the vacancy alert component.
    const { filterOptions } = useFilterOptions();
    const {
        certificationsFilter,
        departmentsFilter,
        maximumHoursFilter,
        minimumHoursFilter,
        zzpHoursFilter,
        regionsFilter,
        employmentTypeFilter
    } = filterOptions;

    useBodyScrollLock(showFilters);

    useEffect(() => {
        if (initialLoad && typeof window !== "undefined") {
            const searchParam = new URL(
                window.document.location as unknown as string
            ).searchParams?.get("search");

            if (searchParam && searchParam !== searchTerm) {
                setSearchTerm(searchParam);
            }

            setInitialLoad(false);
        }
    }, [initialLoad, searchTerm, vacancies]);

    useEffect(() => {
        let newState = structuredClone(initialVacancies);

        if (!calculatingFilters) {
            setCalculatingFilters(true);

            //
            // Searching within
            if (searchTerm !== null && newState) {
                newState = newState.filter(
                    (v: VacancyProps) =>
                        v.title &&
                        v.title
                            .toLowerCase()
                            .includes((searchTerm || "").toLowerCase())
                );
                newState.length === 0
                    ? setNoResults(true)
                    : setNoResults(false);
            }

            if (destinationsWithDistances?.length) {
                newState = newState.filter((v: VacancyProps) => {
                    const city = isZzp ? v.client.town : v.city;
                    const { distance } = router.query;

                    // If there's no distance param in the query, we don't need to filter on distance
                    if (!distance) {
                        return true;
                    }

                    const matchedDestination = destinationsWithDistances?.find(
                        destination =>
                            destination.destination
                                ?.toLowerCase()
                                .replace(", nederland", "") ===
                            city?.toLowerCase()
                    );

                    if (!matchedDestination) return false;

                    return (
                        matchedDestination?.distance <=
                        parseInt(distance as string)
                    );
                });
            }

            if (regionsFilter.length) {
                newState = newState.filter((v: VacancyProps) => {
                    const city = isZzp ? v.client.town : v.city;
                    return regionsFilter.includes(city || "");
                });
            }

            //
            // Checking hours
            if (isZzp) {
                if (zzpHoursFilter.length > 0) {
                    newState = newState.filter(v =>
                        zzpHoursFilter.includes(
                            parseInt(v.uren_per_week?.slice(0, 2) || "0")
                        )
                    );
                }
            } else {
                if (minimumHoursFilter && minimumHoursFilter > 0) {
                    newState = newState.filter(
                        (v: VacancyProps) =>
                            v.min_hours &&
                            (v.min_hours as number) >= minimumHoursFilter
                    );
                }
                if (maximumHoursFilter && maximumHoursFilter < 40) {
                    newState = newState.filter(
                        (v: VacancyProps) =>
                            v.max_hours &&
                            (v.max_hours as number) <= maximumHoursFilter
                    );
                }
                if (departmentsFilter.length) {
                    newState = newState.filter((v: VacancyProps) =>
                        departmentsFilter.includes(
                            v.department ? v.department : ""
                        )
                    );
                }

                if (certificationsFilter.length) {
                    newState = newState.filter((v: VacancyProps) => {
                        return certificationsFilter.includes(
                            v.education_code || ""
                        );
                    });
                }

                if (employmentTypeFilter.length) {
                    newState = newState.filter((v: VacancyProps) => {
                        return employmentTypeFilter.includes(
                            v.employmentType || ""
                        );
                    });
                }
            }

            // Setting new ones.
            const removeDuplicates = uniq(newState);
            setVacancies(removeDuplicates);
            setCalculatingFilters(false);
        }
    }, [
        certificationsFilter,
        departmentsFilter,
        zzpHoursFilter,
        initialVacancies,
        maximumHoursFilter,
        minimumHoursFilter,
        searchTerm,
        isZzp,
        calculatingFilters,
        regionsFilter,
        destinationsWithDistances,
        router.query,
        employmentTypeFilter
    ]);

    const possibleCertifications = useMemo(() => {
        return initialVacancies.reduce((acc: Array<string>, vaca) => {
            const education = vaca.education_code;
            if (!education || acc.includes(education)) return acc;

            return [...acc, education];
        }, []);
    }, [initialVacancies]);

    return (
        <Wrapper>
            {isMobileView && (
                <>
                    <MobileContent visible={showFilters}>
                        <VacancyOverviewFilters
                            label={label}
                            searchTerm={searchTerm}
                            setSearchTerm={value => setSearchTerm(value)}
                            setCalculatingFilters={value =>
                                setCalculatingFilters(value)
                            }
                            zzp={zzp}
                            setZzp={value => setZzp(value)}
                            departments={departments}
                            employmentTypes={employmentTypes}
                            setLoading={value => setLoading(value)}
                            vacanciesAmount={vacancies.length}
                            closeFilters={() => setShowFilters(false)}
                            showFilters={showFilters}
                            regions={regions}
                            possibleCertifications={possibleCertifications}
                            setDestinationsWithDistances={
                                setDestinationsWithDistances
                            }
                            queryHasBeenMade={!!destinationsWithDistances}
                        />
                    </MobileContent>
                </>
            )}
            <StyledContainer>
                <Left>
                    <LeftContent>
                        <MobileOnly>
                            <Button
                                buttonType="primary"
                                onClick={() => setShowFilters(true)}
                            >
                                Filters
                            </Button>
                        </MobileOnly>

                        <DesktopOnly>
                            <VacancyOverviewFilters
                                label={label}
                                searchTerm={searchTerm}
                                setSearchTerm={value => setSearchTerm(value)}
                                setCalculatingFilters={value =>
                                    setCalculatingFilters(value)
                                }
                                zzp={zzp}
                                setZzp={value => setZzp(value)}
                                departments={departments}
                                employmentTypes={employmentTypes}
                                setLoading={value => setLoading(value)}
                                regions={regions}
                                possibleCertifications={possibleCertifications}
                                setDestinationsWithDistances={
                                    setDestinationsWithDistances
                                }
                                queryHasBeenMade={!!destinationsWithDistances}
                            />
                        </DesktopOnly>
                    </LeftContent>
                </Left>

                <Right>
                    <RightTitle>{label}</RightTitle>
                    <RightDescription>
                        {!loading &&
                            `${
                                vacancies.length
                            } passende ${label.toLowerCase()} gevonden op basis
                        van jouw zoekopdracht.`}
                    </RightDescription>

                    {calculatingFilters || loading ? (
                        <EmptyCard calculatingFilters loading />
                    ) : (
                        <>
                            {!noResults && (
                                <Cards>
                                    {vacancies &&
                                        vacancies
                                            .sort(sortByDate)
                                            .slice(0, maxShown)
                                            .map((v: VacancyProps) =>
                                                generateVacancyOverviewCard(
                                                    v,
                                                    zzp,
                                                    departmentSalaries
                                                )
                                            )}
                                </Cards>
                            )}
                            {maxShown < vacancies.length && (
                                <StyledButton
                                    buttonType="secondary"
                                    onClick={() =>
                                        maxShown < vacancies.length &&
                                        setMaxShown(maxShown + 6)
                                    }
                                    icon={ARROW_DOWN}
                                    reversedOrder
                                >
                                    {` Nog ${
                                        vacancies.length - maxShown
                                    } ${label.toLowerCase()}
                                    laden`}
                                </StyledButton>
                            )}
                        </>
                    )}
                    {vacancies.length === 0 &&
                        !calculatingFilters &&
                        !loading && <EmptyCard isZzp={isZzp} />}
                </Right>
            </StyledContainer>
        </Wrapper>
    );
}
